import { ApiError, api } from '@/lib/api';
import { cn } from '@/lib/cn';
import {
  DASHBOARD_DIRECTION_PRESETS,
  DASHBOARD_INTENT_PRESETS,
  type DashboardAuditIssue,
  type DashboardColumnProfile,
  type DashboardDetail,
  type DashboardDirection,
  type DashboardIntent,
  deriveDirection,
} from '@innermate/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertCircle, AlertTriangle, Info, Loader2, Palette, X } from 'lucide-react';
import { useMemo, useState } from 'react';

interface Props {
  dashboardId: string;
  onClose: () => void;
  /** owner 일 때만 직접 편집 가능. dashboard 가 있어야 picker 표시. */
  dashboard?: DashboardDetail;
}

/**
 * Dashv2 §21 renderProfile + §22 renderQualityCard 의 React 화.
 * "분석" 버튼 클릭 시 view 페이지 오른쪽에 슬라이드 인. viewer 권한으로 saved_query 재실행
 * 결과를 받아 컬럼별 dtype/missing/distinct/min·max/top_values + A~D 등급 표시.
 */
export function DashboardProfilePanel({ dashboardId, onClose, dashboard }: Props) {
  const profileQuery = useQuery({
    queryKey: ['dashboards', 'profile', dashboardId],
    queryFn: () => api.dashboardProfile(dashboardId),
    staleTime: 5 * 60_000,
  });

  return (
    <aside className="fixed right-0 top-0 bottom-0 z-40 w-[420px] max-w-[90vw] flex flex-col border-l border-border bg-surface shadow-2xl">
      <header className="flex h-12 shrink-0 items-center gap-2 border-b border-border px-3">
        <h3 className="flex-1 text-sm font-semibold">분석</h3>
        <button
          type="button"
          onClick={onClose}
          aria-label="분석 패널 닫기"
          className="grid size-7 place-items-center rounded-md text-foreground-muted hover:bg-surface-muted hover:text-foreground"
        >
          <X className="size-4" />
        </button>
      </header>

      <div className="flex-1 min-h-0 overflow-y-auto p-3 space-y-4">
        {dashboard ? <DesignPickerSection dashboard={dashboard} /> : null}

        {profileQuery.isLoading ? (
          <div className="flex items-center justify-center py-8 text-sm text-foreground-muted">
            <Loader2 className="mr-2 size-4 animate-spin" /> 분석 중…
          </div>
        ) : profileQuery.isError ? (
          <div className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-3 text-xs text-destructive">
            {profileQuery.error instanceof ApiError
              ? profileQuery.error.message
              : '분석을 불러오지 못했습니다.'}
          </div>
        ) : profileQuery.data ? (
          <ProfileBody
            audit={profileQuery.data.audit}
            columns={profileQuery.data.columns}
            rowCount={profileQuery.data.rowCount}
            truncated={profileQuery.data.truncated}
          />
        ) : null}
      </div>
    </aside>
  );
}

function DesignPickerSection({ dashboard }: { dashboard: DashboardDetail }) {
  const qc = useQueryClient();
  const owner = dashboard.isOwner;
  const currentDirection = (dashboard.spec.direction ?? 'modern-minimal') as DashboardDirection;
  const currentIntent = (dashboard.spec.intent ?? 'executive') as DashboardIntent;
  const [accentHex, setAccentHex] = useState('');
  const [applyError, setApplyError] = useState<string | null>(null);

  const derived = useMemo(() => {
    if (!accentHex.trim()) return null;
    return deriveDirection({ accentHex: accentHex.trim() });
  }, [accentHex]);

  const settingsMutation = useMutation({
    mutationFn: (body: { direction?: DashboardDirection; intent?: DashboardIntent }) =>
      api.dashboardSettings(dashboard.id, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['dashboards', 'detail', dashboard.id] });
      qc.invalidateQueries({ queryKey: ['dashboards', 'mine'] });
    },
  });

  const applyBrandMutation = useMutation({
    mutationFn: async () => {
      if (!derived) throw new Error('유효한 hex 가 아닙니다.');
      const newSpec = JSON.parse(JSON.stringify(dashboard.spec)) as typeof dashboard.spec;
      const applyToSection = (sections: { widgets: { kind: string; colors?: string[] }[] }[]) => {
        for (const s of sections) {
          for (const w of s.widgets) {
            if (w.kind === 'chart') w.colors = [...derived.palette];
          }
        }
      };
      if (newSpec.tabs?.length) for (const t of newSpec.tabs) applyToSection(t.sections);
      else if (newSpec.sections) applyToSection(newSpec.sections);
      await api.dashboardSetSpec(dashboard.id, { spec: newSpec });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['dashboards', 'detail', dashboard.id] });
      setAccentHex('');
      setApplyError(null);
    },
    onError: (err) => setApplyError(err instanceof Error ? err.message : '적용 실패'),
  });

  return (
    <section className="space-y-3 rounded-lg border border-border bg-surface-muted/30 p-3">
      <h4 className="text-xs font-medium">설계</h4>

      <div>
        <div className="text-[11px] text-foreground-muted mb-1.5">설계 방향 (Intent)</div>
        <div className="grid grid-cols-2 gap-1.5">
          {DASHBOARD_INTENT_PRESETS.map((p) => {
            const active = currentIntent === p.id;
            return (
              <button
                key={p.id}
                type="button"
                disabled={!owner || settingsMutation.isPending}
                onClick={() => settingsMutation.mutate({ intent: p.id })}
                className={cn(
                  'rounded-md border px-2 py-1.5 text-left text-[11px] transition',
                  active
                    ? 'border-accent bg-accent/10 text-foreground'
                    : 'border-border bg-surface hover:bg-surface-muted',
                  !owner && 'opacity-60 cursor-not-allowed',
                )}
              >
                <div className="font-medium">{p.label}</div>
                <div className="mt-0.5 text-[10px] text-foreground-muted line-clamp-2">
                  {p.hint}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      <div>
        <div className="text-[11px] text-foreground-muted mb-1.5">비주얼 디렉션</div>
        <div className="space-y-1">
          {DASHBOARD_DIRECTION_PRESETS.map((p) => {
            const active = currentDirection === p.id;
            return (
              <button
                key={p.id}
                type="button"
                disabled={!owner || settingsMutation.isPending}
                onClick={() => settingsMutation.mutate({ direction: p.id })}
                className={cn(
                  'flex w-full items-center gap-2 rounded-md border px-2 py-1.5 text-left transition',
                  active
                    ? 'border-accent bg-accent/10'
                    : 'border-border bg-surface hover:bg-surface-muted',
                  !owner && 'opacity-60 cursor-not-allowed',
                )}
              >
                <div className="flex shrink-0 gap-0.5">
                  {p.swatch.map((c, i) => (
                    <span
                      key={`${p.id}-${i}-${c}`}
                      className="block size-3 rounded-sm border border-border/50"
                      style={{ background: c }}
                    />
                  ))}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-[11px] font-medium">{p.label}</div>
                  <div className="text-[10px] text-foreground-muted truncate">{p.hint}</div>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {owner ? (
        <div>
          <div className="text-[11px] text-foreground-muted mb-1.5 flex items-center gap-1">
            <Palette className="size-3" /> 브랜드 색 (hex)
          </div>
          <div className="flex gap-1.5">
            <input
              type="text"
              value={accentHex}
              onChange={(e) => setAccentHex(e.target.value)}
              placeholder="#3b82f6"
              className="flex-1 rounded-md border border-border bg-surface px-2 py-1 text-[11px] font-mono"
            />
            <button
              type="button"
              disabled={!derived || applyBrandMutation.isPending}
              onClick={() => applyBrandMutation.mutate()}
              className="rounded-md border border-border bg-surface px-2 py-1 text-[11px] hover:bg-surface-muted disabled:opacity-50"
            >
              적용
            </button>
          </div>
          {derived ? (
            <div className="mt-1.5 flex gap-0.5">
              {derived.palette.map((c, i) => (
                <span
                  key={`palette-${i}-${c}`}
                  className="block h-3 flex-1 rounded-sm border border-border/50"
                  style={{ background: c }}
                />
              ))}
            </div>
          ) : null}
          {applyError ? (
            <div className="mt-1 text-[10px] text-destructive">{applyError}</div>
          ) : null}
        </div>
      ) : (
        <div className="rounded-md border border-border bg-surface px-2 py-1.5 text-[10px] text-foreground-muted">
          소유자만 설계를 변경할 수 있습니다.
        </div>
      )}
    </section>
  );
}

export interface BodyProps {
  audit: Awaited<ReturnType<typeof api.dashboardProfile>>['audit'];
  columns: DashboardColumnProfile[];
  rowCount: number;
  truncated: boolean;
}

export function ProfileBody({ audit, columns, rowCount, truncated }: BodyProps) {
  const gradeColor =
    audit.grade === 'A'
      ? 'bg-success/10 text-success border-success/30'
      : audit.grade === 'B'
        ? 'bg-accent/10 text-accent border-accent/30'
        : audit.grade === 'C'
          ? 'bg-amber-500/10 text-amber-600 border-amber-500/30'
          : 'bg-destructive/10 text-destructive border-destructive/30';

  return (
    <div className="space-y-4">
      {/* Quality 카드 */}
      <section className="rounded-lg border border-border bg-surface-muted/40 p-3">
        <div className="flex items-center gap-3">
          <div
            className={cn(
              'inline-grid size-12 place-items-center rounded-full border text-2xl font-bold',
              gradeColor,
            )}
            aria-label={`데이터 품질 ${audit.grade}`}
          >
            {audit.grade}
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-xs text-foreground-muted">데이터 품질</div>
            <div className="text-sm font-medium">{audit.score} / 100</div>
            <div className="text-[11px] text-foreground-muted mt-0.5">
              {rowCount.toLocaleString('ko')}행 × {columns.length}컬럼
              {truncated ? ' (10,000행 상한 도달)' : ''}
            </div>
          </div>
        </div>

        <div className="mt-3 grid grid-cols-3 gap-2 text-center text-[11px]">
          <Metric label="평균 결측" value={`${audit.metrics.avgMissing}%`} />
          <Metric label="중복 행" value={audit.metrics.duplicates.toLocaleString('ko')} />
          <Metric
            label="시간 컬럼"
            value={
              audit.metrics.hasTimeCol
                ? audit.metrics.timeGapDetected
                  ? '갭 있음'
                  : '있음'
                : '없음'
            }
          />
        </div>
      </section>

      {/* 이슈 목록 */}
      {audit.issues.length > 0 ? (
        <section>
          <h4 className="text-xs font-medium mb-2">진단</h4>
          <ul className="space-y-1.5">
            {audit.issues.map((iss, idx) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: 시점 정렬 + 동일 순서
              <IssueRow key={idx} issue={iss} />
            ))}
          </ul>
        </section>
      ) : null}

      {/* 컬럼 프로파일 */}
      <section>
        <h4 className="text-xs font-medium mb-2">컬럼별 프로파일</h4>
        <ul className="space-y-1.5">
          {columns.map((c) => (
            <ColumnRow key={c.name} col={c} />
          ))}
        </ul>
      </section>
    </div>
  );
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md border border-border bg-surface px-2 py-1.5">
      <div className="text-foreground-muted">{label}</div>
      <div className="font-medium text-foreground text-xs">{value}</div>
    </div>
  );
}

function IssueRow({ issue }: { issue: DashboardAuditIssue }) {
  const Icon =
    issue.severity === 'error' ? AlertCircle : issue.severity === 'warn' ? AlertTriangle : Info;
  const color =
    issue.severity === 'error'
      ? 'text-destructive'
      : issue.severity === 'warn'
        ? 'text-amber-600'
        : 'text-foreground-muted';
  return (
    <li className="flex gap-2 rounded-md border border-border bg-surface px-2.5 py-1.5 text-xs">
      <Icon className={cn('size-3.5 shrink-0 mt-0.5', color)} aria-hidden />
      <div className="min-w-0 flex-1">
        {issue.column ? (
          <span className="font-mono text-[10px] text-foreground-muted mr-1">{issue.column}</span>
        ) : null}
        <span>{issue.message}</span>
        {issue.hint ? (
          <div className="text-[11px] text-foreground-muted mt-0.5">{issue.hint}</div>
        ) : null}
      </div>
    </li>
  );
}

function ColumnRow({ col }: { col: DashboardColumnProfile }) {
  return (
    <li className="rounded-md border border-border bg-surface px-2.5 py-1.5 text-xs">
      <div className="flex items-center gap-2">
        <span className="font-mono text-foreground">{col.name}</span>
        <span className="text-[10px] uppercase tracking-wide text-foreground-muted">
          {col.dtype}
        </span>
        <span className="ml-auto text-[10px] text-foreground-muted">
          {col.n_unique.toLocaleString('ko')} 고유 · {col.missing_pct}% 결측
        </span>
      </div>
      <div className="mt-0.5 text-[11px] text-foreground-muted">
        {col.role_candidate === 'measure'
          ? '측정값'
          : col.role_candidate === 'time_dimension'
            ? '시간 차원'
            : col.role_candidate === 'filter_dimension'
              ? '필터 차원'
              : col.role_candidate === 'breakdown_dimension'
                ? '브레이크다운 차원'
                : '식별자'}
        {col.dtype === 'numeric' && col.min !== undefined && col.max !== undefined ? (
          <span className="ml-2">
            {String(col.min)} ~ {String(col.max)}
            {col.mean !== undefined ? ` (평균 ${col.mean})` : ''}
          </span>
        ) : null}
        {col.dtype === 'datetime' && col.min !== undefined && col.max !== undefined ? (
          <span className="ml-2">
            {String(col.min)} ~ {String(col.max)}
          </span>
        ) : null}
      </div>
      {col.top_values && col.top_values.length > 0 ? (
        <div className="mt-1 flex flex-wrap gap-1">
          {col.top_values.slice(0, 5).map((tv) => (
            <span
              key={tv.value}
              className="inline-flex items-center gap-1 rounded bg-surface-muted px-1.5 py-0.5 text-[10px]"
              title={`${tv.count.toLocaleString('ko')}건 (${tv.pct.toFixed(1)}%)`}
            >
              <span className="truncate max-w-[120px]">{tv.value}</span>
              <span className="text-foreground-muted">{tv.pct.toFixed(0)}%</span>
            </span>
          ))}
        </div>
      ) : null}
    </li>
  );
}
